import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { productService } from '../../services/productService';
import { getWarehouse } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import { detectKardexInconsistencies, normalizeKardexInconsistencyCountFrom, blocksKardexRecalculateFromModal } from '../../utils/kardexMath';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const KARDEX_ZERO_QTY_EVENTS = new Set(['descuento_obtenido', 'ajuste_costo']);

const kardexEventLabel = (event) => {
  const map = {
    compra: 'Compra',
    venta: 'Venta',
    saldo_inicial: 'Saldo Inicial',
    traspaso: 'Traspaso',
    recepcion: 'Recepción',
    salida_produccion: 'Salida a producción',
    entrada_produccion: 'Entrada de producción',
    entrada_producto_terminado: 'Entrada producto terminado',
    ajuste_inventario: 'Ajuste inventario',
    ajuste_costo: 'Ajuste de costo',
    transferencia_gratuita: 'Transferencia Gratuita',
    descuento_obtenido: 'Descuento Obtenido',
    descuento_concedido: 'Descuento Concedido',
    descuento_con_mercaderia: 'Descuento con Mercadería',
    consumo: 'Consumo',
    devolucion: 'Devolución',
  };
  return map[event] || event;
};

const showsKardexEntryQuantity = (movement) =>
  movement.movement_type === 'entrada'
  && movement.kardex_event !== 'devolucion'
  && !(
    KARDEX_ZERO_QTY_EVENTS.has(movement.kardex_event)
    && Math.abs(Number(movement.quantity)) < 0.0001
  );

const showsKardexEntryUnitPrice = (movement) =>
  movement.movement_type === 'entrada'
  && movement.kardex_event !== 'devolucion'
  && !KARDEX_ZERO_QTY_EVENTS.has(movement.kardex_event);

const getMovementReference = (movement) => {
  if (movement.reference && movement.reference !== '-') {
    return movement.reference;
  }
  if (movement.kardex_event === 'devolucion' && movement.consumption_exit_guide_code) {
    return movement.consumption_exit_guide_code;
  }
  if (movement.document) {
    if (movement.document.series && movement.document.number) {
      return `${movement.document.series}-${movement.document.number}`;
    }
    return movement.document.code || '-';
  }
  if (movement.purchase_order) {
    return movement.purchase_order.code || '-';
  }
  return '-';
};

const KardexDetail = () => {
  const navigate = useNavigate();
  const { productId, warehouseId } = useParams();
  const { machineryEnabled } = useModuleSettings();
  const [product, setProduct] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [kardexMovements, setKardexMovements] = useState([]);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [finalSummary, setFinalSummary] = useState(null);
  const [finalMovementsSummary, setFinalMovementsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isInconsistencyModalOpen, setIsInconsistencyModalOpen] = useState(false);
  const [selectedInconsistencies, setSelectedInconsistencies] = useState([]);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [exportComplete, setExportComplete] = useState(false);
  const [inconsistencyCountFrom, setInconsistencyCountFrom] = useState(null);
  
  // Obtener mes/año de los query params o usar mes actual por defecto
  const searchParams = new URLSearchParams(window.location.search);
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(
    searchParams.get('year') ? parseInt(searchParams.get('year')) : now.getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState(
    searchParams.get('month') ? parseInt(searchParams.get('month')) : now.getMonth() + 1
  );
  const [viewFromMonth, setViewFromMonth] = useState(
    searchParams.get('from_month') === 'true'
  );
  
  const hasLoadedData = useRef(false);
  const lastKardexParams = useRef(null);

  const loadProductAndWarehouse = async () => {
    try {
      const [productResponse, warehouseResponse] = await Promise.all([
        productService.getProduct(productId),
        getWarehouse(warehouseId)
      ]);

      if (productResponse.success) {
        setProduct(productResponse.data);
      }

      if (warehouseResponse.success) {
        setWarehouse(warehouseResponse.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const loadKardexDetail = useCallback(async () => {
    try {
      setLoading(true);
      const kardexResponse = await reportService.getKardexDetail(productId, warehouseId, selectedYear, selectedMonth, viewFromMonth);

      if (kardexResponse.success) {
        setKardexMovements(kardexResponse.data || []);
        setPreviousSummary(kardexResponse.previous_summary || null);
        setFinalSummary(kardexResponse.final_summary || null);
        setFinalMovementsSummary(kardexResponse.final_movements_summary || null);
        setInconsistencyCountFrom(normalizeKardexInconsistencyCountFrom(kardexResponse.count_from));
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el detalle de kardex',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [productId, warehouseId, selectedYear, selectedMonth, viewFromMonth]);

  useEffect(() => {
    if (!hasLoadedData.current && productId && warehouseId) {
      hasLoadedData.current = true;
      loadProductAndWarehouse();
    }
  }, [productId, warehouseId]);

  useEffect(() => {
    if (productId && warehouseId) {
      // Crear una clave única para los parámetros de kardex
      const kardexKey = `${productId}-${warehouseId}-${selectedYear}-${selectedMonth}-${viewFromMonth}`;
      
      // Solo cargar si los parámetros han cambiado (evita doble llamada en StrictMode)
      if (lastKardexParams.current !== kardexKey) {
        lastKardexParams.current = kardexKey;
        loadKardexDetail();
      }
      
      // Actualizar URL con los parámetros de mes/año
      const params = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear
      });
      if (viewFromMonth) {
        params.append('from_month', 'true');
      }
      const newUrl = `/reports/kardex/${productId}/${warehouseId}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [productId, warehouseId, selectedYear, selectedMonth, viewFromMonth, loadKardexDetail]);

  // Usar saldos del backend (q3/q4) para evitar falsas inconsistencias por redondeo JS
  const movementsWithStock = kardexMovements.map((movement) => {
    const stockAfter = movement.stock_after ?? 0;
    const totalValueAfter = movement.total_value_after ?? 0;
    const averageCostAfter = movement.average_cost_after ?? 0;

    const inconsistencies = detectKardexInconsistencies(movement, {
      product,
      stockBefore: movement.stock_before,
      countFrom: inconsistencyCountFrom,
    });

    return {
      ...movement,
      stockAfter,
      totalValueAfter,
      averageCostAfter,
      stockBefore: movement.stock_before,
      totalValueBefore: movement.total_value_before,
      averageCostBefore: movement.average_cost_before,
      inconsistencies,
    };
  });

  // Usar resumen final del backend, asegurando que tenga todas las propiedades necesarias
  const summary = finalSummary ? {
    stockByCondition: finalSummary.stock_by_condition || { good: 0, damaged: 0, defective: 0 },
    totalStock: finalSummary.stock || 0,
    totalValue: finalSummary.total_value || 0,
    averageCost: finalSummary.average_cost || 0
  } : {
    stockByCondition: { good: 0, damaged: 0, defective: 0 },
    totalStock: 0,
    totalValue: 0,
    averageCost: 0
  };

  // Meses en español
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Generar años disponibles (últimos 5 años hasta el actual)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Función para exportar Kardex completo (todos los movimientos)
  const exportToExcelComplete = async () => {
    try {
      // Cargar todos los movimientos sin filtro de mes
      setLoading(true);
      const kardexResponse = await reportService.getKardexDetail(productId, warehouseId, null, null, false, true);

      if (!kardexResponse.success) {
        setToast({
          isOpen: true,
          message: 'Error al cargar los movimientos completos',
          type: 'error'
        });
        setLoading(false);
        return;
      }

      const allMovements = kardexResponse.data || [];
      const exportCountFrom = normalizeKardexInconsistencyCountFrom(kardexResponse.count_from);

      const movementsWithStock = allMovements.map((movement) => ({
        ...movement,
        stockAfter: movement.stock_after ?? 0,
        totalValueAfter: movement.total_value_after ?? 0,
        averageCostAfter: movement.average_cost_after ?? 0,
        inconsistencies: detectKardexInconsistencies(movement, {
          product,
          countFrom: exportCountFrom,
        }),
      }));

      // Generar Excel con todos los movimientos
      await generateExcelFile(movementsWithStock, null, 'KARDEX COMPLETO');

      setToast({
        isOpen: true,
        message: 'Kardex completo exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar kardex completo:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el kardex completo',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para generar el archivo Excel
  const generateExcelFile = async (dataToExport, previousSummaryData, titleSuffix = '') => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Kardex Detallado');

    // Título
    worksheet.mergeCells('A1:Q1');
    const titleRow = worksheet.getRow(1);
    titleRow.height = 30;
    titleRow.getCell(1).value = `${titleSuffix} - ${product?.name || 'Producto'} - ${warehouse?.code || 'Almacén'}`;
    titleRow.getCell(1).font = { size: 16, bold: true };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

    // Información del reporte
    worksheet.mergeCells('A2:Q2');
    const infoRow = worksheet.getRow(2);
    infoRow.getCell(1).value = titleSuffix === 'KARDEX COMPLETO' 
      ? 'Todos los movimientos registrados'
      : `Período: ${viewFromMonth ? `Desde ${monthNames[selectedMonth - 1]} ${selectedYear} hasta la fecha` : `${monthNames[selectedMonth - 1]} ${selectedYear}`}`;
    infoRow.getCell(1).font = { size: 12 };
    infoRow.getCell(1).alignment = { horizontal: 'center' };

    // Fecha de exportación
    worksheet.mergeCells('A3:Q3');
    const dateRow = worksheet.getRow(3);
    dateRow.getCell(1).value = `Exportado el: ${new Date().toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' })}`;
    dateRow.getCell(1).font = { size: 10, italic: true };
    dateRow.getCell(1).alignment = { horizontal: 'center' };

    // Encabezados de la tabla
    const headerRow = worksheet.getRow(5);
    const plateOffset = machineryEnabled ? 1 : 0;
    const headers = [
      'Fecha', 'Hora', 'Evento', 'Guía',
      ...(machineryEnabled ? ['Placa'] : []),
      'Entidad', 'Referencia',
      'ENTRADAS - Unidades', 'ENTRADAS - Costo Unit', 'ENTRADAS - Valor Total',
      'SALIDAS - Unidades', 'SALIDAS - Costo Unit', 'SALIDAS - Valor Total',
      'EXISTENCIAS - Unidades', 'EXISTENCIAS - Costo Unit', 'EXISTENCIAS - Valor Total',
      'Condición', 'Revisión'
    ];
    const totalCols = headers.length;

    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true, size: 11 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    headerRow.height = 30;

    // Datos
    let currentRow = 6;

    // Procesar datos (puede incluir resumen anterior y movimientos)
    dataToExport.forEach((item) => {
      // Si es el resumen anterior
      if (item.isPreviousSummary && item.previousSummary) {
        const prevRow = worksheet.getRow(currentRow);
        prevRow.getCell(1).value = 'MOVIMIENTOS ANTERIORES (RESUMEN)';
        prevRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(currentRow, 1, currentRow, 6 + plateOffset);
        prevRow.getCell(13 + plateOffset).value = item.previousSummary.stock || 0;
        prevRow.getCell(14 + plateOffset).value = item.previousSummary.average_cost || 0;
        prevRow.getCell(15 + plateOffset).value = item.previousSummary.total_value || 0;
        prevRow.getCell(16 + plateOffset).value = `Bueno: ${item.previousSummary.stock_by_condition?.good || 0}`;
        prevRow.getCell(17 + plateOffset).value = '-';
        prevRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE3F2FD' }
        };
        for (let i = 1; i <= totalCols; i++) {
          prevRow.getCell(i).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        }
        currentRow++;
        return;
      }
      
      // Es un movimiento normal
      const movement = item;
      const row = worksheet.getRow(currentRow);
      
      // Fecha
      row.getCell(1).value = formatDateLocaleLima(movement.movement_date);
      
      // Hora
      row.getCell(2).value = movement.movement_time || '-';
      
      // Evento
      const eventMap = {
        'compra': 'Compra',
        'venta': 'Venta',
        'saldo_inicial': 'Saldo Inicial',
        'traspaso': 'Traspaso',
        'recepcion': 'Recepción',
        'ajuste_inventario': 'Ajuste inventario',
        'ajuste_costo': 'Ajuste de costo',
        'transferencia_gratuita': 'Transferencia Gratuita',
        'descuento_obtenido': 'Descuento Obtenido',
        'descuento_concedido': 'Descuento Concedido',
        'descuento_con_mercaderia': 'Descuento con Mercadería',
        'consumo': 'Consumo',
        'devolucion': 'Devolución'
      };
      row.getCell(3).value = eventMap[movement.kardex_event] || movement.kardex_event;
      
      // Guía
      row.getCell(4).value = movement.entry_guide_code || '-';

      let col = 5;
      if (machineryEnabled) {
        row.getCell(col).value = movement.vehicle_license_plate || '-';
        col += 1;
      }
      
      // Entidad
      row.getCell(col).value = movement.supplier_name || '-';
      col += 1;
      
      // Referencia (traspaso: almacén destino; devolución: guía; si no, documento u OC)
      row.getCell(col).value = getMovementReference(movement);
      col += 1;
      
      // ENTRADAS (devoluciones no se muestran aquí, van a salidas como negativo)
      if (movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion') {
        row.getCell(col).value = showsKardexEntryQuantity(movement) ? movement.quantity : '-';
        row.getCell(col + 1).value = showsKardexEntryUnitPrice(movement) ? movement.unit_price : '-';
        row.getCell(col + 2).value = movement.total;
      } else {
        row.getCell(col).value = '-';
        row.getCell(col + 1).value = '-';
        row.getCell(col + 2).value = '-';
      }
      
      // SALIDAS (salidas positivas; devoluciones: solo cantidad y valor total en negativo, costo unitario positivo)
      if (movement.movement_type === 'salida') {
        row.getCell(col + 3).value = movement.quantity;
        row.getCell(col + 4).value = movement.unit_price;
        row.getCell(col + 5).value = movement.total;
      } else if (movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion') {
        row.getCell(col + 3).value = -movement.quantity;
        row.getCell(col + 4).value = movement.unit_price;
        row.getCell(col + 5).value = -movement.total;
      } else {
        row.getCell(col + 3).value = '-';
        row.getCell(col + 4).value = '-';
        row.getCell(col + 5).value = '-';
      }
      
      // EXISTENCIAS
      row.getCell(col + 6).value = movement.stockAfter || 0;
      row.getCell(col + 7).value = movement.averageCostAfter !== 0 ? movement.averageCostAfter : '-';
      row.getCell(col + 8).value = movement.totalValueAfter || 0;
      
      // Condición
      const conditionMap = {
        'good': 'Bueno',
        'damaged': 'Dañado',
        'defective': 'Defectuoso',
        'reusable': 'Reutilizable',
        'non_reusable': 'No reutilizable'
      };
      row.getCell(col + 9).value = conditionMap[movement.condition] || movement.condition;
      
      // Revisión (Inconsistencias)
      const reviewCol = col + 10;
      if (movement.inconsistencies && movement.inconsistencies.length > 0) {
        const inconsistencyMessages = movement.inconsistencies.map(inc => {
          const typeMap = {
            'costo_salida': 'Costo Unitario Incorrecto',
            'costo_recepcion': 'Costo de Recepción Incorrecto',
            'stock_negativo': 'Stock Negativo',
            'valor_negativo': 'Valor Total Negativo'
          };
          return `${typeMap[inc.type] || inc.type}: ${inc.message}`;
        });
        row.getCell(reviewCol).value = inconsistencyMessages.join('\n');
        row.getCell(reviewCol).font = { color: { argb: 'FFFF0000' }, bold: true };
        row.getCell(reviewCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFE0E0' }
        };
      } else {
        row.getCell(reviewCol).value = '-';
      }
      
      // Aplicar bordes
      for (let i = 1; i <= totalCols; i++) {
        row.getCell(i).border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
      
      currentRow++;
    });

    // Ajustar ancho de columnas
    worksheet.columns = [
      { width: 12 }, // Fecha
      { width: 10 }, // Hora
      { width: 20 }, // Evento
      { width: 12 }, // Guía
      ...(machineryEnabled ? [{ width: 12 }] : []), // Placa
      { width: 25 }, // Entidad
      { width: 15 }, // Referencia
      { width: 15 }, // ENTRADAS - Unidades
      { width: 15 }, // ENTRADAS - Costo Unit
      { width: 15 }, // ENTRADAS - Valor Total
      { width: 15 }, // SALIDAS - Unidades
      { width: 15 }, // SALIDAS - Costo Unit
      { width: 15 }, // SALIDAS - Valor Total
      { width: 15 }, // EXISTENCIAS - Unidades
      { width: 15 }, // EXISTENCIAS - Costo Unit
      { width: 15 }, // EXISTENCIAS - Valor Total
      { width: 12 }, // Condición
      { width: 30 }  // Revisión
    ];

    // Formatear números y ajustar altura de filas para revisión
    const numericCols = [7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => n + plateOffset);
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 5) {
        numericCols.forEach(colNum => {
          const cell = row.getCell(colNum);
          if (cell.value !== '-' && cell.value !== null && cell.value !== undefined) {
            const baseCol = colNum - plateOffset;
            if (baseCol === 8 || baseCol === 11 || baseCol === 14) {
              cell.numFmt = '#,##0.0000';
            } else if (baseCol === 9 || baseCol === 12 || baseCol === 15) {
              cell.numFmt = '#,##0.0000';
            } else {
              cell.numFmt = '#,##0.000';
            }
          }
        });
        
        // Ajustar altura de fila si hay inconsistencias
        const reviewCell = row.getCell(17 + plateOffset);
        if (reviewCell.value && reviewCell.value !== '-') {
          row.height = Math.max(20, (reviewCell.value.toString().split('\n').length * 15) + 10);
          reviewCell.alignment = { vertical: 'top', wrapText: true };
        }
      }
    });

    // Generar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = titleSuffix === 'KARDEX COMPLETO'
      ? `Kardex_Completo_${product?.name || 'Producto'}_${warehouse?.code || 'Almacen'}.xlsx`
      : `Kardex_Detallado_${product?.name || 'Producto'}_${warehouse?.code || 'Almacen'}_${monthNames[selectedMonth - 1]}_${selectedYear}.xlsx`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Función para exportar a Excel (mes actual o filtrado, o completo según checkbox)
  const exportToExcel = async () => {
    try {
      // Si el checkbox está marcado, exportar kardex completo
      if (exportComplete) {
        await exportToExcelComplete();
        return;
      }

      // Preparar datos con resumen anterior si existe
      const dataToExport = [];
      
      // Agregar fila de movimientos anteriores si existe
      if (previousSummary && previousSummary.stock !== 0 && previousSummary.stock > 0) {
        dataToExport.push({
          isPreviousSummary: true,
          previousSummary: previousSummary
        });
      }
      
      // Agregar movimientos del mes
      dataToExport.push(...movementsWithStock);
      
      await generateExcelFile(dataToExport, previousSummary, 'KARDEX DETALLADO');

      setToast({
        isOpen: true,
        message: 'Kardex exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el kardex',
        type: 'error'
      });
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/reports/kardex')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver a Kardex
        </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Detalle de Kardex</h1>
              <p className="text-gray-600 mt-1">
                {product && warehouse && (
                  <>
                    Producto: <span className="font-medium">{product.name}</span> | 
                    Almacén: <span className="font-medium">{warehouse.code} - {warehouse.name}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="exportComplete"
                  checked={exportComplete}
                  onChange={(e) => setExportComplete(e.target.checked)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="exportComplete" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Exportar Kardex Completo
                </label>
              </div>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exportar a Excel
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap mt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Mes:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {monthNames.map((month, index) => (
                  <option key={index + 1} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Año:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="viewFromMonth"
                checked={viewFromMonth}
                onChange={(e) => setViewFromMonth(e.target.checked)}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor="viewFromMonth" className="text-sm font-medium text-gray-700 cursor-pointer">
                Ver a partir de este mes
              </label>
            </div>
          </div>
          {inconsistencyCountFrom ? (
            <div className="mt-4 max-w-3xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Inconsistencias visibles desde{' '}
              <strong>
                {formatDateLocaleLima(inconsistencyCountFrom.date)}
                {inconsistencyCountFrom.time ? ` ${String(inconsistencyCountFrom.time).slice(0, 5)}` : ''}
              </strong>
              . Los movimientos anteriores no muestran alertas en la columna Revisión.
            </div>
          ) : null}
        </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">Cargando movimientos...</p>
          </div>
        </div>
      ) : kardexMovements.length === 0 && viewFromMonth ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center text-gray-500">
            No hay movimientos registrados
          </div>
        </div>
      ) : (
        <>
          {/* Título del Mes */}
          <div className="bg-teal-50 rounded-lg shadow-sm border border-teal-200 p-3 mb-4">
            <h2 className="text-lg font-semibold text-teal-900">
              {viewFromMonth 
                ? `Movimientos desde ${monthNames[selectedMonth - 1]} ${selectedYear} hasta la fecha`
                : `Movimientos del Mes: ${monthNames[selectedMonth - 1]} ${selectedYear}`
              }
            </h2>
          </div>

          {/* Resumen del Mes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1">Bueno</div>
                <div className="text-lg font-bold text-gray-900">
                  {summary.stockByCondition.good.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1">Dañado</div>
                <div className="text-lg font-bold text-gray-900">
                  {summary.stockByCondition.damaged.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1">Defectuoso</div>
                <div className="text-lg font-bold text-gray-900">
                  {summary.stockByCondition.defective.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1">Saldo</div>
                <div className="text-lg font-bold text-teal-600">
                  {summary.totalStock.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {product?.unit_of_measure || ''}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  PU: {summary.averageCost !== 0 
                    ? `S/ ${summary.averageCost.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                    : '-'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-600 mb-1">Total</div>
                <div className="text-lg font-bold text-teal-600">
                  S/ {summary.totalValue.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de Kardex */}
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
           <div className="overflow-x-auto max-w-full">
             <table className="w-full divide-y divide-gray-200 border-collapse text-xs" style={{ fontSize: '0.75rem' }}>
               <thead className="bg-gray-50">
                 <tr>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '80px' }}>Fecha</th>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '70px' }}>Hora</th>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '100px' }}>Evento</th>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '80px' }}>Guía</th>
                   {machineryEnabled && (
                     <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '90px' }}>Placa</th>
                   )}
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '120px' }}>Entidad</th>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '100px' }}>Referencia</th>
                   <th colSpan="3" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-green-100">ENTRADAS</th>
                   <th colSpan="3" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-red-100">SALIDAS</th>
                   <th colSpan="3" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-teal-100">EXISTENCIAS</th>
                   <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300" style={{ minWidth: '80px' }}>Condición</th>
                   <th rowSpan="2" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-yellow-50" style={{ minWidth: '60px' }}>Revisión</th>
                 </tr>
                 <tr>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-green-100" style={{ minWidth: '70px' }}>Unidades</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-green-100" style={{ minWidth: '80px' }}>Costo Unit</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-green-100" style={{ minWidth: '80px' }}>Valor Total</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-red-100" style={{ minWidth: '70px' }}>Unidades</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-red-100" style={{ minWidth: '80px' }}>Costo Unit</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-red-100" style={{ minWidth: '80px' }}>Valor Total</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-teal-100" style={{ minWidth: '80px' }}>Unidades</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-teal-100" style={{ minWidth: '80px' }}>Costo Unit</th>
                   <th className="px-1 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300 bg-teal-100" style={{ minWidth: '80px' }}>Valor Total</th>
                 </tr>
               </thead>
               <tbody className="bg-white divide-y divide-gray-200">
                 {/* Fila de Movimientos Anteriores / Saldo inicial del mes (modo mensual sin movimientos: siempre mostrar saldos iniciales) */}
                 {previousSummary && (previousSummary.stock > 0 || (!viewFromMonth && kardexMovements.length === 0)) && (
                   <tr className="bg-blue-50 hover:bg-blue-100">
                     <td colSpan={machineryEnabled ? 7 : 6} className="px-2 py-2 text-xs font-semibold text-blue-900 border border-gray-300">
                       MOVIMIENTOS ANTERIORES (RESUMEN)
                     </td>
                     {/* ENTRADAS - vacías */}
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-green-50">-</td>
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-green-50">-</td>
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-green-50">-</td>
                     {/* SALIDAS - vacías */}
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-red-50">-</td>
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-red-50">-</td>
                     <td className="px-1 py-2 text-xs text-gray-500 text-right border border-gray-300 bg-red-50">-</td>
                     {/* EXISTENCIAS - valores del resumen anterior */}
                     <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-blue-900 text-right border border-gray-300 bg-teal-50">
                       {(previousSummary.stock || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {product?.unit_of_measure || ''}
                     </td>
                     <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-blue-900 text-right border border-gray-300 bg-teal-50">
                       {previousSummary.average_cost !== 0 
                         ? `S/ ${(previousSummary.average_cost || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                         : '-'}
                     </td>
                     <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-blue-900 text-right border border-gray-300 bg-teal-50">
                       S/ {(previousSummary.total_value || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                     </td>
                     {/* CONDICIÓN - resumen por condición */}
                     <td className="px-2 py-2 border border-gray-300">
                       <div className="flex flex-col gap-1">
                         {previousSummary.stock_by_condition?.good > 0 && (
                           <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                             Bueno: {(previousSummary.stock_by_condition.good || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                           </span>
                         )}
                         {previousSummary.stock_by_condition?.damaged > 0 && (
                           <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                             Dañado: {(previousSummary.stock_by_condition.damaged || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                           </span>
                         )}
                         {previousSummary.stock_by_condition?.defective > 0 && (
                           <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                             Defectuoso: {(previousSummary.stock_by_condition.defective || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                           </span>
                         )}
                         {(!previousSummary.stock_by_condition || 
                           (previousSummary.stock_by_condition.good === 0 && 
                            previousSummary.stock_by_condition.damaged === 0 && 
                            previousSummary.stock_by_condition.defective === 0)) && (
                           <span className="text-xs text-gray-400">-</span>
                         )}
                       </div>
                     </td>
                     {/* REVISIÓN - sin inconsistencias en resumen anterior */}
                     <td className="px-2 py-2 text-center border border-gray-300 bg-yellow-50">-</td>
                   </tr>
                 )}
                 
                 {movementsWithStock.length === 0 && !previousSummary ? (
                   <tr>
                     <td colSpan={machineryEnabled ? 18 : 17} className="px-4 py-8 text-center text-gray-500 border border-gray-300">
                       No hay movimientos registrados
                     </td>
                   </tr>
                 ) : movementsWithStock.length > 0 ? (
                   movementsWithStock.map((movement) => (
                     <tr key={movement.id} className="hover:bg-gray-50">
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {formatDateLocaleLima(movement.movement_date)}
                       </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {movement.movement_time || '-'}
                       </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {kardexEventLabel(movement.kardex_event)}
                       </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {movement.entry_guide_code || '-'}
                       </td>
                       {machineryEnabled && (
                         <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                           {movement.vehicle_license_plate || '-'}
                         </td>
                       )}
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {movement.supplier_name || '-'}
                       </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                         {movement.kardex_event === 'traspaso' || (movement.reference && movement.reference !== '-') ? (
                           getMovementReference(movement)
                         ) : movement.kardex_event === 'devolucion' && movement.consumption_exit_guide_code ? (
                           movement.consumption_exit_guide_code
                         ) : movement.document ? (
                           <div>
                             {movement.document.series && movement.document.number ? (
                               <div>
                                 <div className="font-medium">{movement.document.series}-{movement.document.number}</div>
                                 <div className="text-xs text-gray-400">{movement.document.code}</div>
                               </div>
                             ) : (
                               <div>{movement.document.code}</div>
                             )}
                           </div>
                         ) : movement.purchase_order ? (
                           <div>
                             <div className="font-medium">{movement.purchase_order.code}</div>
                             <div className="text-xs text-gray-400">Orden de Compra</div>
                           </div>
                         ) : '-'}
                       </td>
                       {/* ENTRADAS */}
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                         {showsKardexEntryQuantity(movement) ? (
                           <>
                             {movement.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                             {movement.original_quantity !== movement.quantity && Math.abs(Number(movement.original_quantity)) >= 0.0001 && (
                               <div className="text-xs text-gray-400">
                                 ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                               </div>
                             )}
                           </>
                         ) : '-'}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                         {showsKardexEntryUnitPrice(movement)
                           ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                           : '-'}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                         {movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion' ? `S/ ${movement.total.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                       </td>
                       {/* SALIDAS */}
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                         {movement.movement_type === 'salida' ? (
                           <>
                             {movement.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                             {movement.original_quantity !== movement.quantity && (
                               <div className="text-xs text-gray-400">
                                 ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                               </div>
                             )}
                           </>
                         ) : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? (
                           <>
                             {(-movement.quantity).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                             {movement.original_quantity !== movement.quantity && (
                               <div className="text-xs text-gray-400">
                                 ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                               </div>
                             )}
                           </>
                         ) : '-'}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                         {movement.movement_type === 'salida' ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                         {movement.movement_type === 'salida' ? `S/ ${movement.total.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? `S/ ${(-movement.total).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                       </td>
                       {/* EXISTENCIAS */}
                       <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-gray-900 text-right border border-gray-300 bg-teal-50">
                         {movement.stockAfter.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {product?.unit_of_measure || ''}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-gray-900 text-right border border-gray-300 bg-teal-50">
                         {movement.averageCostAfter !== 0 
                           ? `S/ ${movement.averageCostAfter.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                           : '-'}
                       </td>
                       <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-gray-900 text-right border border-gray-300 bg-teal-50">
                         S/ {movement.totalValueAfter.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                       </td>
                       <td className="px-2 py-2 whitespace-nowrap border border-gray-300">
                         <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                           movement.condition === 'good' 
                             ? 'bg-green-100 text-green-800' 
                             : movement.condition === 'damaged'
                             ? 'bg-yellow-100 text-yellow-800'
                             : movement.condition === 'defective'
                             ? 'bg-red-100 text-red-800'
                             : movement.condition === 'reusable'
                             ? 'bg-blue-100 text-blue-800'
                             : movement.condition === 'non_reusable'
                             ? 'bg-purple-100 text-purple-800'
                             : 'bg-gray-100 text-gray-800'
                         }`}>
                           {movement.condition === 'good' ? 'Bueno' :
                            movement.condition === 'damaged' ? 'Dañado' :
                            movement.condition === 'defective' ? 'Defectuoso' :
                            movement.condition === 'reusable' ? 'Reutilizable' :
                            movement.condition === 'non_reusable' ? 'No reutilizable' : movement.condition}
                         </span>
                       </td>
                       {/* REVISIÓN - mostrar señales de inconsistencia */}
                       <td className="px-2 py-2 text-center border border-gray-300 bg-yellow-50">
                         {movement.inconsistencies && movement.inconsistencies.length > 0 ? (
                           <button
                             onClick={() => {
                               setSelectedInconsistencies(movement.inconsistencies);
                               setSelectedMovement(movement);
                               setIsInconsistencyModalOpen(true);
                             }}
                             className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer w-full"
                             title="Haz clic para ver detalles"
                           >
                             <svg 
                               className="w-5 h-5 text-red-600" 
                               fill="none" 
                               stroke="currentColor" 
                               viewBox="0 0 24 24"
                             >
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                             </svg>
                             <span className="text-xs text-red-600 font-semibold">
                               {movement.inconsistencies.length}
                             </span>
                           </button>
                         ) : (
                           <span className="text-xs text-gray-400">-</span>
                         )}
                       </td>
                     </tr>
                   ))
                 ) : null}
               </tbody>
            </table>
          </div>
        </div>

          {/* Movimientos Finales - Resumen (si es mes histórico o "ver a partir de mes") */}
          {finalMovementsSummary && (
            <div className="bg-purple-50 rounded-lg shadow-sm border border-purple-200 p-4 mt-4">
              <h2 className="text-lg font-semibold text-purple-900 mb-3">
                {viewFromMonth ? 'Saldo Final' : 'Movimientos Finales (Después del Mes)'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Bueno</div>
                  <div className="text-lg font-bold text-gray-900">
                    {(finalMovementsSummary.stock_by_condition?.good || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Dañado</div>
                  <div className="text-lg font-bold text-gray-900">
                    {(finalMovementsSummary.stock_by_condition?.damaged || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Defectuoso</div>
                  <div className="text-lg font-bold text-gray-900">
                    {(finalMovementsSummary.stock_by_condition?.defective || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Saldo Final</div>
                  <div className="text-lg font-bold text-purple-600">
                    {(finalMovementsSummary.stock || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {product?.unit_of_measure || ''}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    PU: {finalMovementsSummary.average_cost !== 0 
                      ? `S/ ${(finalMovementsSummary.average_cost || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                      : '-'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Valor Total</div>
                  <div className="text-lg font-bold text-purple-600">
                    S/ {(finalMovementsSummary.total_value || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      {/* Modal de Inconsistencias */}
      {isInconsistencyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-red-50 border-b border-red-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-xl font-bold text-red-900">Inconsistencias Detectadas</h2>
              </div>
              <button
                onClick={() => setIsInconsistencyModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Información del Movimiento */}
              {selectedMovement && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Información del Movimiento</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Fecha:</span>
                      <span className="ml-2 font-medium">{formatDateLocaleLima(selectedMovement.movement_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Hora:</span>
                      <span className="ml-2 font-medium">{selectedMovement.movement_time || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Evento:</span>
                      <span className="ml-2 font-medium">
                          {selectedMovement.kardex_event === 'compra' ? 'Compra' :
                         selectedMovement.kardex_event === 'venta' ? 'Venta' :
                         selectedMovement.kardex_event === 'saldo_inicial' ? 'Saldo Inicial' :
                         selectedMovement.kardex_event === 'traspaso' ? 'Traspaso' :
                         selectedMovement.kardex_event === 'recepcion' ? 'Recepción' :
                           selectedMovement.kardex_event === 'salida_produccion' ? 'Salida a producción' :
                           selectedMovement.kardex_event === 'entrada_produccion' ? 'Entrada de producción' :
                           selectedMovement.kardex_event === 'entrada_producto_terminado' ? 'Entrada producto terminado' :
                         selectedMovement.kardex_event === 'ajuste_inventario' ? 'Ajuste inventario' :
                         selectedMovement.kardex_event === 'ajuste_costo' ? 'Ajuste de costo' :
                         selectedMovement.kardex_event === 'transferencia_gratuita' ? 'Transferencia Gratuita' :
                         selectedMovement.kardex_event === 'descuento_obtenido' ? 'Descuento Obtenido' :
                         selectedMovement.kardex_event === 'descuento_concedido' ? 'Descuento Concedido' :
                         selectedMovement.kardex_event === 'descuento_con_mercaderia' ? 'Descuento con Mercadería' :
                         selectedMovement.kardex_event === 'consumo' ? 'Consumo' :
                         selectedMovement.kardex_event}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Guía:</span>
                      <span className="ml-2 font-medium">{selectedMovement.entry_guide_code || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Entidad:</span>
                      <span className="ml-2 font-medium">{selectedMovement.supplier_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Cantidad:</span>
                      <span className="ml-2 font-medium">
                        {selectedMovement.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {product?.unit_of_measure || ''}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Inconsistencias */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Inconsistencias ({selectedInconsistencies.length})
                </h3>
                <div className="space-y-3">
                  {selectedInconsistencies.map((inconsistency, index) => (
                    <div
                      key={index}
                      className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
                    >
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">
                            {inconsistency.type === 'costo_salida' && 'Costo Unitario Incorrecto'}
                            {inconsistency.type === 'costo_recepcion' && 'Costo de Recepción Incorrecto'}
                            {inconsistency.type === 'stock_negativo' && 'Stock Negativo'}
                            {inconsistency.type === 'valor_negativo' && 'Valor Total Negativo'}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {inconsistency.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              {blocksKardexRecalculateFromModal(selectedMovement, selectedInconsistencies) ? (
                <p className="text-sm text-amber-700 flex-1 mr-4">
                  Este movimiento no se puede recalcular desde aquí porque proviene de un documento (factura/boleta), no de una guía de entrada. Para corregir el costo, edite el documento correspondiente.
                </p>
              ) : null}
              <button
                onClick={() => {
                  if (selectedMovement) {
                    const movementDate = new Date(selectedMovement.movement_date);
                    const month = movementDate.getMonth() + 1;
                    const year = movementDate.getFullYear();
                    navigate(`/reports/kardex/${productId}/${warehouseId}/recalculate?from_month=${month}&from_year=${year}`);
                  }
                }}
                disabled={blocksKardexRecalculateFromModal(selectedMovement, selectedInconsistencies)}
                className="px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
              >
                Recalcular
              </button>
              <button
                onClick={() => setIsInconsistencyModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KardexDetail;

